import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { listAllResource, updateResource, unwrapItems } from "services/adminApi";

const statusLabels = {
  new: "جديدة",
  in_progress: "قيد المتابعة",
  closed: "مغلقة",
};

const categoryLabels = {
  general: "استفسار عام",
  technical: "مشكلة تقنية",
  suggestion: "ملاحظة أو اقتراح",
  other: "أخرى",
};

function ContactMessagesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    setError("");
    listAllResource("contact-messages")
      .then((payload) => setItems(unwrapItems(payload)))
      .catch((requestError) => setError(requestError.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((item) => {
      if (status && item.status !== status) return false;
      const sentDate = String(item.createdAt || "").slice(0, 10);
      if (fromDate && sentDate < fromDate) return false;
      if (toDate && sentDate > toDate) return false;
      if (!needle) return true;
      return [item.displayName, item.email, item.phoneNumber, item.message]
        .join(" ")
        .toLowerCase()
        .includes(needle);
    });
  }, [items, search, status, fromDate, toDate]);

  const statusCounts = useMemo(
    () =>
      Object.keys(statusLabels).reduce(
        (counts, key) => ({ ...counts, [key]: items.filter((item) => item.status === key).length }),
        {}
      ),
    [items]
  );

  const changeStatus = async (nextStatus) => {
    if (!selected || saving) return;
    setSaving(true);
    setError("");
    try {
      const updated = await updateResource("contact-messages", selected.messageId, {
        status: nextStatus,
      });
      setItems((current) =>
        current.map((item) => (item.messageId === updated.messageId ? updated : item))
      );
      setSelected(updated);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3} dir="rtl">
        <Typography variant="h4" fontWeight="bold">
          رسائل التواصل
        </Typography>
        <Typography color="text" mb={3}>
          رسائل المستخدمين الواردة من تطبيق مسافر. يمكن متابعة الحالة دون تعديل محتوى الرسالة.
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} mb={2}>
          {Object.entries(statusLabels).map(([value, label]) => (
            <Chip
              key={value}
              label={`${label} (${statusCounts[value] || 0})`}
              color={status === value ? "primary" : "default"}
              variant={status === value ? "filled" : "outlined"}
              onClick={() => setStatus((current) => (current === value ? "" : value))}
            />
          ))}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={2}>
          <TextField
            fullWidth
            label="بحث بالاسم أو البريد أو الرسالة"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <FormControl sx={{ minWidth: 190 }}>
            <InputLabel>الحالة</InputLabel>
            <Select
              value={status}
              label="الحالة"
              onChange={(event) => setStatus(event.target.value)}
            >
              <MenuItem value="">الكل</MenuItem>
              {Object.entries(statusLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            type="date"
            label="من تاريخ"
            value={fromDate}
            onChange={(event) => setFromDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date"
            label="إلى تاريخ"
            value={toDate}
            onChange={(event) => setToDate(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {(search || status || fromDate || toDate) && (
            <Button
              variant="text"
              onClick={() => {
                setSearch("");
                setStatus("");
                setFromDate("");
                setToDate("");
              }}
            >
              مسح الفلاتر
            </Button>
          )}
        </Stack>

        {!loading && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            {filtered.length} {filtered.length === 1 ? "رسالة مطابقة" : "رسائل مطابقة"}
          </Typography>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {filtered.map((item) => (
              <Box
                key={item.messageId}
                onClick={() => setSelected(item)}
                sx={{
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  border: "1px solid #E1ECEF",
                  p: 2,
                  cursor: "pointer",
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={2}
                >
                  <Box minWidth={0}>
                    <Typography fontWeight="bold">{item.displayName || "مستخدم مسافر"}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {categoryLabels[item.category] || item.category} · {item.email}
                    </Typography>
                    <Typography variant="body2" mt={1} noWrap>
                      {item.message}
                    </Typography>
                  </Box>
                  <Chip
                    label={statusLabels[item.status] || item.status}
                    color={
                      item.status === "new"
                        ? "error"
                        : item.status === "in_progress"
                        ? "warning"
                        : "success"
                    }
                  />
                </Stack>
              </Box>
            ))}
            {!filtered.length && <Alert severity="info">لا توجد رسائل مطابقة.</Alert>}
          </Stack>
        )}
      </Box>

      <Dialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="sm"
        dir="rtl"
      >
        {selected && (
          <>
            <DialogTitle>رسالة من {selected.displayName || "مستخدم مسافر"}</DialogTitle>
            <DialogContent>
              <Stack spacing={1.5} mt={1}>
                <Typography>
                  <strong>النوع:</strong> {categoryLabels[selected.category] || selected.category}
                </Typography>
                <Typography>
                  <strong>البريد:</strong> {selected.email || "—"}
                </Typography>
                <Typography>
                  <strong>الهاتف:</strong> {selected.phoneNumber || "—"}
                </Typography>
                <Typography>
                  <strong>تاريخ الإرسال:</strong> {selected.createdAt || "—"}
                </Typography>
                <Box sx={{ bgcolor: "#F7FAFA", borderRadius: 2, p: 2, whiteSpace: "pre-wrap" }}>
                  {selected.message}
                </Box>
                <FormControl fullWidth>
                  <InputLabel>الحالة</InputLabel>
                  <Select
                    value={selected.status}
                    label="الحالة"
                    disabled={saving}
                    onChange={(event) => changeStatus(event.target.value)}
                  >
                    {Object.entries(statusLabels).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelected(null)}>إغلاق</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </DashboardLayout>
  );
}

export default ContactMessagesPage;
